// pages/Dashboard/Analytics.jsx - Analytics page with tabs and charts
import React, { useState, useEffect } from 'react';
import analyticsService from '../../services/analyticsService';
import { HazardChart, TrendChart } from '../../components/Dashboard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { format, subDays, subMonths, parseISO } from 'date-fns';

const COLORS = ['#059669', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0'];
const DIFFICULTY_COLORS = {
  1: '#10B981',
  2: '#34D399',
  3: '#F59E0B',
  4: '#F97316',
  5: '#EF4444'
};

const tabs = [
  { id: 'hazards', name: 'Hazards' },
  { id: 'metrics', name: 'Metrics' },
  { id: 'trends', name: 'Trends' }
];

const dateRanges = [
  { id: '7d', name: 'Last 7 days', days: 7 },
  { id: '30d', name: 'Last 30 days', days: 30 },
  { id: '90d', name: 'Last 90 days', days: 90 },
  { id: '1y', name: 'Last year', days: 365 }
];

export default function Analytics() {
  const [activeTab, setActiveTab] = useState('hazards');
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [hazards, setHazards] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [hazardTrends, setHazardTrends] = useState(null);

  const getDateRange = () => {
    const range = dateRanges.find(r => r.id === dateRange);
    const endDate = new Date();
    const startDate = subDays(endDate, range?.days || 30);
    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd')
    };
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { startDate, endDate } = getDateRange();

      if (activeTab === 'hazards') {
        const [hazardsData] = await Promise.all([
          analyticsService.getHazards({ startDate, endDate, limit: 10 })
        ]);
        setHazards(hazardsData);
      } else if (activeTab === 'metrics') {
        const metricsData = await analyticsService.getMetrics({ startDate, endDate });
        setMetrics(metricsData);
      } else if (activeTab === 'trends') {
        const [submissionTrends, hazardTrendsData] = await Promise.all([
          analyticsService.getTrends('submissions', 'daily', { startDate, endDate, limit: 30 }),
          analyticsService.getTrends('hazards', 'daily', { startDate, endDate, limit: 30 })
        ]);
        setTrends(submissionTrends);
        setHazardTrends(hazardTrendsData);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderHazardsTab = () => {
    if (!hazards) return null;

    return (
      <div className="space-y-6">
        {/* Top hazards chart */}
        <HazardChart
          data={hazards.topHazards || []}
          loading={loading}
        />

        {/* Summary stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">Total Hazards Identified</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {hazards.topHazards?.reduce((sum, h) => sum + parseInt(h.count, 10), 0) || 0}
              </dd>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">Avg Hazards per Job</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {hazards.averageHazardsPerJob?.toFixed(1) || '0'}
              </dd>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">Most Common Hazard</dt>
              <dd className="mt-1 text-lg font-semibold text-gray-900 truncate">
                {hazards.topHazards?.[0]?.hazard || 'N/A'}
              </dd>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMetricsTab = () => {
    if (!metrics) return null;

    // Transform difficulty distribution for pie chart
    const climbingData = metrics.climbingDistribution?.map((item, index) => ({
      name: `Level ${item.difficulty}`,
      value: parseInt(item.count, 10),
      fill: DIFFICULTY_COLORS[item.difficulty] || COLORS[index % COLORS.length]
    })) || [];

    const riggingData = metrics.riggingDistribution?.map((item, index) => ({
      name: `Level ${item.difficulty}`,
      value: parseInt(item.count, 10),
      fill: DIFFICULTY_COLORS[item.difficulty] || COLORS[index % COLORS.length]
    })) || [];

    return (
      <div className="space-y-6">
        {/* Average metrics */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">Average Climbing Difficulty</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {metrics.averageClimbingDifficulty?.toFixed(1) || 'N/A'}
                <span className="text-lg text-gray-500 font-normal"> / 5</span>
              </dd>
              <div className="mt-2 flex items-center">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${((metrics.averageClimbingDifficulty || 0) / 5) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">Average Rigging Difficulty</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {metrics.averageRiggingDifficulty?.toFixed(1) || 'N/A'}
                <span className="text-lg text-gray-500 font-normal"> / 5</span>
              </dd>
              <div className="mt-2 flex items-center">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${((metrics.averageRiggingDifficulty || 0) / 5) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Difficulty distribution charts */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Climbing Difficulty Distribution</h3>
            {climbingData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={climbingData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {climbingData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rigging Difficulty Distribution</h3>
            {riggingData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riggingData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {riggingData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTrendsTab = () => {
    return (
      <div className="space-y-6">
        <TrendChart
          data={trends?.data || []}
          title="Submissions Over Time"
          loading={loading}
          color="#059669"
        />

        <TrendChart
          data={hazardTrends?.data || []}
          title="Hazards Identified Over Time"
          loading={loading}
          color="#F59E0B"
        />
      </div>
    );
  };

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading analytics</h3>
            <p className="mt-2 text-sm text-red-700">{error}</p>
            <button
              onClick={fetchData}
              className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Detailed insights into safety assessment data
          </p>
        </div>

        {/* Date range selector */}
        <div className="mt-4 sm:mt-0">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
          >
            {dateRanges.map((range) => (
              <option key={range.id} value={range.id}>
                {range.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      )}

      {/* Tab content */}
      {!loading && (
        <>
          {activeTab === 'hazards' && renderHazardsTab()}
          {activeTab === 'metrics' && renderMetricsTab()}
          {activeTab === 'trends' && renderTrendsTab()}
        </>
      )}
    </div>
  );
}
